/**
 * The chart's stored table (`part.table`): its cells as the table shows them, its CSV, and saving a
 * file. The values are the ones stored with the chart, never a control's current position.
 */
import type { WireChartTable } from "../protocol/protocol";

type Column = WireChartTable["columns"][number];
type Cell = WireChartTable["rows"][number][string];

const MONEY = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const COUNT = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const PERCENT = new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 });
const MONTH = new Intl.DateTimeFormat("en-US", {
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});
const DAY = new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeZone: "UTC" });

/** A cell as the app shows such a value: money in full, months and days written out. */
export function formatCell(column: Column, value: Cell): string {
  if (value === null || value === undefined) return "";
  if (column.type === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") {
    if (column.unit === "usd") return MONEY.format(value);
    if (column.unit === "pct") return `${PERCENT.format(value)}%`;
    return COUNT.format(value);
  }
  const text = String(value);
  if (column.type === "month" && /^\d{4}-\d{2}$/.test(text)) {
    return MONTH.format(new Date(`${text}-01T00:00:00Z`));
  }
  if (column.type === "date" && /^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return DAY.format(new Date(`${text}T00:00:00Z`));
  }
  return text;
}

/** Text a spreadsheet would run as a formula; a plain number (a negative balance) is not one. */
const FORMULA = /^[=+\-@\t\r]/;
const NUMBER = /^-?\d+(\.\d+)?([eE][+-]?\d+)?$/;

function csvField(value: Cell): string {
  let text = value === null || value === undefined ? "" : String(value);
  // Series and funder names come from statements and the model: never a formula when opened.
  const formula = FORMULA.test(text) && !NUMBER.test(text);
  if (formula) text = `'${text}`;
  return formula || /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** The table as CSV: the column labels, then the stored values as they are (numbers unformatted). */
export function tableCsv(table: WireChartTable): string {
  const lines = [table.columns.map((column) => csvField(column.label))];
  for (const row of table.rows)
    lines.push(table.columns.map((column) => csvField(row[column.key])));
  return lines.map((cells) => cells.join(",")).join("\r\n") + "\r\n";
}

/** A file name from the chart's title: letters, digits and dashes. */
export function fileName(title: string, extension: string): string {
  const base = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return `${base || "chart"}.${extension}`;
}

/** Saves `href` (a data or object URL) as `name`. */
export function saveFile(href: string, name: string): void {
  const link = document.createElement("a");
  link.href = href;
  link.download = name;
  document.body.append(link);
  link.click();
  link.remove();
}

const CSV_URL_LIFETIME_MS = 1000;

export function saveCsv(table: WireChartTable, title: string): void {
  // A byte order mark first, so Excel reads it as UTF-8 (accented names, the minus sign).
  const blob = new Blob(["\uFEFF", tableCsv(table)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  saveFile(url, fileName(title, "csv"));
  // Some browsers read the file after the click returns: let go of it a moment later.
  window.setTimeout(() => URL.revokeObjectURL(url), CSV_URL_LIFETIME_MS);
}
